import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import { apiRoute } from '../../constants/api-constants';
import { useAuth } from '../../context/auth-provider';
import { Onboarding } from '../../interfaces/onboarding-interfaces';
import { resolvePostAuthRoute } from '../../utils/post-auth-route';
import { clearAllLocalDrafts } from '../../utils/onboarding-drafts';
import { ROLES, isStaff } from '../../auth/roles';
import { LogoMark } from '../logo/logo-mark';
import { RightArrow } from '../icons/right-arrow';
import { OnboardingProgress } from './onboarding-progress';
import { InviteForm } from './invite/invite-form';
import { ProfileStep } from './steps/profile-step';
import {
  AutoHeight,
  Collapse,
  Shake,
  StateSwitch,
  StepDirection,
  StepTransition,
} from '../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  ButtonRow,
  CenteredAction,
  STEP_LAYER_STYLE,
  InfoBanner,
  HelperText,
  Field,
  Label,
  Input,
  FieldError,
  ProgressSegments,
  ProgressSegment,
  HeadingSkeleton,
  FieldSkeleton,
  SkeletonButton,
  SkeletonGroup,
} from './onboarding.styled';

/* THE SHAPE OF A STEP, BEFORE THE STEP. A progress bar, a heading, two fields
   and a button — roughly what every step renders — so the card is already the
   right height while /onboarding is read, and the real step crossfades into
   place instead of landing on a one-line card and pushing it open. */
const SetupSkeleton: React.FC = () => (
  <SkeletonGroup label="Loading your setup">
    <ProgressSegments aria-hidden="true">
      <ProgressSegment />
      <ProgressSegment />
      <ProgressSegment />
    </ProgressSegments>
    <HeadingSkeleton />
    <FieldSkeleton />
    <FieldSkeleton />
    <SkeletonButton aria-hidden="true" />
  </SkeletonGroup>
);

// A request that never reached the server is a connection problem, not a
// rejection: the step was not refused, it was not asked. That is the one failure
// worth holding on to and retrying by itself, so it gets its own banner instead
// of the generic "something went wrong" toast.
/** How long "Your address is confirmed" stays before the wizard moves on. */
export const VERIFIED_PAUSE_MS = 1500;

const unreachable = (error: unknown): boolean =>
  axios.isAxiosError(error) && !error.response;

// Owner first-run wizard. Reads /onboarding, renders the current step, and
// advances via /onboarding/step, finishing via /onboarding/complete. The
// property_manager/concierge/resident flows (Phase 5) reuse this same shell.
//
// Resuming is the shell's job, not each step's. `current` lives on the user row,
// so signing back in — on any device — lands on the step that was open; the
// unsubmitted contents of that step are handled separately by
// use-onboarding-draft.ts.
const OnboardingFlowPage: React.FC = () => {
  const { user, permissions, setOnboarding } = useAuth();
  const navigate = useNavigate();
  const [ob, setOb] = useState<Onboarding | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [buildings, setBuildings] = useState<{ id: number; name: string }[]>(
    []
  );
  const [buildingsLoaded, setBuildingsLoaded] = useState(false);
  // Set when a step advance (or the finish) never reached the server. Holds the
  // request so it can be sent again the moment the connection is back, rather
  // than leaving the owner to guess whether their click counted.
  const [stalled, setStalled] = useState<
    | { kind: 'step'; step: string; extra?: Record<string, string> }
    | { kind: 'complete' }
    | null
  >(null);
  // The company name (Plan 3 F2), asked on the owner's first step and
  // prefilled from the first building. Seeded once, when /onboarding answers.
  const [orgName, setOrgName] = useState('');
  const [orgError, setOrgError] = useState('');
  const [orgAttempt, setOrgAttempt] = useState(0);
  // Which way the card is travelling: Back slides the previous step in from
  // the left, everything else arrives from the right.
  const [direction, setDirection] = useState<StepDirection>('forward');

  // The admin's buildings feed the tenant-invite step's building picker.
  // Re-loaded after each step advance so a building created during onboarding
  // is present by the time the tenant-invite step renders.
  const loadBuildings = useCallback(() => {
    // "is not staff", not "is a resident": since migration 037 a resident's
    // role is NULL, so the old equality was false for exactly the people it was
    // skipping the fetch for and every one of them fired a doomed 403.
    if (!isStaff(user?.role)) {
      setBuildingsLoaded(true); // residents can't list buildings (403) — skip

      return;
    }
    axios
      .get(`${apiRoute}/dashboard/buildings`)
      .then(r =>
        setBuildings(
          (Array.isArray(r.data) ? r.data : []).map(
            (b: { id: number; property_name: string }) => ({
              id: b.id,
              name: b.property_name,
            })
          )
        )
      )
      .catch(() => {
        // non-admins can't list buildings; the tenant step just shows none
      })
      .finally(() => setBuildingsLoaded(true));
    // The role is all it reads. Keyed on the whole `user` object, a new object
    // with the same role re-made this callback, re-fetched the buildings and
    // re-made `advance` — which restarts the verify step's VERIFIED_PAUSE_MS
    // timer, so the step could sit on "Your address is confirmed" for ever.
  }, [user?.role]);
  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  // Reconcile a completed Stripe Checkout on return from the payment step. The
  // wizard hands off to Stripe, which redirects back to /onboarding?checkout=
  // success&session_id=…; this activates the per-unit subscription (belt-and-
  // suspenders to the webhook — the only activation path in local dev). The
  // params are stripped so a refresh doesn't re-run it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkout === 'success' && sessionId) {
      axios
        .post(`${apiRoute}/subscriptions/reconcile`, { session_id: sessionId })
        .then(() => toast.success('Your 30-day trial is active'))
        .catch(() => {
          // the webhook reconciles too — not fatal
        })
        .finally(() =>
          window.history.replaceState({}, '', window.location.pathname)
        );
    } else if (checkout === 'cancel') {
      toast('Checkout canceled — you can add billing anytime.', { icon: 'ℹ️' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${apiRoute}/onboarding`)
      .then(r => {
        if (!alive) return;
        if (r.data?.completed) {
          navigate(resolvePostAuthRoute(user, r.data, permissions), {
            replace: true,
          });

          return;
        }
        setOb(r.data);
        const org = r.data?.organization;
        if (org) setOrgName(org.name || org.suggested_name || '');
      })
      .catch(() => {
        // load failed → ob stays null and the error card renders
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, []);

  const advance = useCallback(
    async (stepKey: string, extra?: Record<string, string>) => {
      setBusy(true);
      try {
        const r = await axios.patch(`${apiRoute}/onboarding/step`, {
          ...extra,
          step: stepKey,
        });
        setOb(r.data);
        setStalled(null);
        loadBuildings();
      } catch (error) {
        const refused = axios.isAxiosError(error) ? error.response : undefined;
        if (unreachable(error)) {
          setStalled({ kind: 'step', step: stepKey, extra });
        } else if (
          stepKey === 'organization_name' &&
          refused?.status === 400 &&
          refused.data?.message
        ) {
          // The server's own sentence about the name, under the field.
          setOrgError(String(refused.data.message));
          setOrgAttempt(n => n + 1);
        } else {
          toast.error('Something went wrong. Please try again.');
        }
      } finally {
        setBusy(false);
      }
    },
    [loadBuildings]
  );

  const finish = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${apiRoute}/onboarding/complete`);
      setOnboarding(r.data);
      setStalled(null);
      // Onboarding is over — nothing parked locally is worth keeping, and
      // leaving it would resurface on a future first-run in the same browser.
      clearAllLocalDrafts(user?.id || null);
      toast.success('You’re all set — welcome to Lobbero');
      // Route each role to its own home, not always the admin dashboard.
      navigate(resolvePostAuthRoute(user, r.data, permissions), {
        replace: true,
      });
    } catch (error) {
      if (unreachable(error)) {
        setStalled({ kind: 'complete' });
      } else if (
        (error as { response?: { data?: { code?: string } } })?.response?.data
          ?.code === 'NO_UNITS'
      ) {
        // The server refuses to finish an account with nothing to work with
        // (onboarding.js, owner flow only): no units means no door to put a
        // resident behind, and an owner who is "done" and can do nothing is
        // worse than one still in setup. Say WHY and send them to the step that
        // fixes it — the generic "finish the required steps" below is a lie
        // here, because every step IS optional and finishing more of them
        // would not clear this.
        // NOT A STEP ANY MORE. This 400 is about the PLAN — the account bought
        // no units — and there is no wizard screen that can fix it, so the
        // reader is sent to billing rather than to a step that would ask them
        // to place units they never purchased.
        toast.error(
          'Your plan has no units yet. Add units to your plan, then finish setup.'
        );
        navigate('/dashboard/billing');
      } else {
        toast.error('Please finish the required steps first.');
      }
    } finally {
      setBusy(false);
    }
    // `permissions` IS a dependency and was missing. resolvePostAuthRoute reads
    // it to route a resident — residency is a permission (portal:read derived
    // from an open occupancy), not a role, since migration 037 — so a stale
    // empty array here sends them to '/' instead of to their portal. It is
    // empty on the first render of any mount that resolves auth after the fact.
  }, [navigate, setOnboarding, user, permissions]);

  // Back online → send the held request again. The step itself is idempotent
  // (marking a done step done is a no-op), so a retry that turns out to have
  // been unnecessary costs nothing.
  useEffect(() => {
    if (!stalled) {
      return undefined;
    }

    const retry = () => {
      if (stalled.kind === 'complete') {
        void finish();

        return;
      }

      void advance(stalled.step, stalled.extra);
    };

    window.addEventListener('online', retry);

    return () => window.removeEventListener('online', retry);
  }, [advance, finish, stalled]);

  const steps = ob ? ob.steps : [];
  const idx = ob
    ? Math.max(
        0,
        steps.findIndex(s => s.key === ob.current)
      )
    : 0;
  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  /**
   * BACK. The wizard had none — not on any step.
   *
   * Every ButtonRow put a bare `<span />` in the slot where a Back control
   * belongs, so the only way to revisit a step was to abandon the wizard. On a
   * flow that asks for a building, a staffing model and invitations, being
   * unable to go and check what you typed two screens ago is the difference
   * between a wizard and a funnel.
   *
   * PATCH /onboarding/step takes a step key and does not care about direction,
   * so moving backwards is the same call that moves forwards — this is a
   * missing control, not a missing capability.
   */
  const previousStep = idx > 0 ? steps[idx - 1] : null;
  const goBack = previousStep
    ? () => {
        setDirection('back');
        void advance(previousStep.key);
      }
    : null;
  const firstName = (user?.full_name || '').split(' ')[0];

  const primaryAction = () => {
    setDirection('forward');

    return isLast ? finish() : advance(step.key);
  };

  const saveOrgName = () => {
    const name = orgName.replace(/\s+/g, ' ').trim();
    if (name.length < 2) {
      setOrgError('Enter your company name.');
      setOrgAttempt(n => n + 1);

      return;
    }
    setOrgError('');
    setDirection('forward');
    void advance('organization_name', { organization_name: name });
  };
  const primaryLabel = isLast ? 'Finish setup' : 'Continue';

  /* THE ADDRESS IS ALREADY CONFIRMED (Plan 3 F3). The mailed link marks the
     step done on the server, but the wizard used to sit on "Check your inbox"
     until Continue was pressed. Now the step says it is done and moves on by
     itself — after a moment, so the sentence is read rather than flashed. */
  const onVerifyStep = step?.key === 'verify_email';
  const verified = onVerifyStep && (!!ob?.email_verified || !!step?.done);
  useEffect(() => {
    if (!verified || stalled) return undefined;
    const timer = setTimeout(() => {
      setDirection('forward');
      void advance('verify_email');
    }, VERIFIED_PAUSE_MS);

    return () => clearTimeout(timer);
  }, [verified, stalled, advance]);

  /* Verified in another tab or on the phone while this one waits: ask again
     when the person comes back to this tab. A read only — it never moves the
     wizard by itself; the effect above does, once the answer says so. */
  useEffect(() => {
    if (!onVerifyStep || verified) return undefined;
    let alive = true;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      axios
        .get(`${apiRoute}/onboarding`)
        .then(r => {
          if (alive && r.data?.email_verified && r.data.current === 'verify_email') {
            setOb(r.data);
          }
        })
        .catch(() => {
          // a failed re-check changes nothing; the step still works by hand
        });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);

    return () => {
      alive = false;
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [onVerifyStep, verified]);

  const loadState = loading ? 'loading' : !ob || !step ? 'error' : 'ready';

  return (
    <Shell>
      <Card wide>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        {/* ONE CARD for the placeholder, the failure and every step: the
            fills crossfade and the card tweens between their heights. */}
        <AutoHeight>
          <StateSwitch state={loadState}>
            {loadState === 'loading' && <SetupSkeleton />}

            {loadState === 'error' && (
              <>
                <Heading>Setup</Heading>
                <HelperText>
                  We couldn’t load your onboarding. Try refreshing.
                </HelperText>
              </>
            )}

            {loadState === 'ready' && (
              <>
                <OnboardingProgress current={idx + 1} total={steps.length} />

                <Collapse open={!!stalled}>
                  <InfoBanner tone="warning">
                    You’re offline, so that didn’t go through. Nothing is lost —
                    we’ve kept this step and everything you’ve typed, and we’ll
                    pick it up again as soon as you’re back online.
                  </InfoBanner>
                </Collapse>

                <StepTransition
                  stepKey={step.key}
                  direction={direction}
                  style={STEP_LAYER_STYLE}
                >
                  {step.key === 'organization_name' && (
                    <form
                      noValidate
                      onSubmit={e => {
                        e.preventDefault();
                        saveOrgName();
                      }}
                    >
                      <Heading>
                        {firstName
                          ? `Welcome, ${firstName}`
                          : 'Welcome to Lobbero'}
                      </Heading>
                      <Subheading>
                        What is your company called? Your team sees this name,
                        and so does Lobbero support when you get in touch.
                      </Subheading>
                      <Field>
                        <Label htmlFor="ob-org-name">Company name</Label>
                        <Shake active={!!orgError} attempt={orgAttempt}>
                          <Input
                            id="ob-org-name"
                            value={orgName}
                            maxLength={120}
                            autoComplete="organization"
                            hasError={!!orgError}
                            onChange={e => {
                              setOrgName(e.target.value);
                              if (orgError) setOrgError('');
                            }}
                          />
                        </Shake>
                      </Field>
                      <FieldError message={orgError || undefined} />
                      <HelperText>
                        You can change it later under Account → Company.
                      </HelperText>
                      <ButtonRow>
                        <span />
                        <PrimaryButton type="submit" disabled={busy}>
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </form>
                  )}

                  {step.key === 'verify_email' && verified && (
                    <>
                      <Heading>Your address is confirmed</Heading>
                      <Subheading>
                        Thank you — taking you to the next step.
                      </Subheading>
                      <ButtonRow>
                        <span />
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={primaryAction}
                        >
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  )}

                  {step.key === 'verify_email' && !verified && (
                    <>
                      <Heading>Confirm your email address</Heading>
                      <Subheading>
                        We sent a verification link to your email. You can
                        verify now or keep going and do it later.
                      </Subheading>
                      <InfoBanner>
                        Check your inbox for a link to confirm your email
                        address.
                      </InfoBanner>
                      <ButtonRow>
                        <SecondaryButton
                          type="button"
                          disabled={busy}
                          onClick={async () => {
                            try {
                              await axios.post(
                                `${apiRoute}/resend-verification`
                              );
                              toast.success('Verification email sent');
                            } catch {
                              toast.error('Could not resend right now.');
                            }
                          }}
                        >
                          Resend email
                        </SecondaryButton>
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={primaryAction}
                        >
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  )}

                  {/* Portfolios are no longer a wizard step: signup already creates the
              org's default one in SQL, and the step only renamed it. They stay a
              concept the owner meets later, from Dashboard → Portfolios. */}

                  {step.key === 'invite_staff' && (
                    <>
                      <Heading>Invite your team</Heading>
                      <Subheading>
                        Add managers and concierge staff. Each person gets an
                        email + QR invite and sets their own password. Add as
                        many as you like.
                      </Subheading>
                      {/* THE ROW IS ABOUT THIS STEP: send one invite, or move on. Skipping
                  the REST of the sequence is a different kind of action, so it
                  sits on its own line underneath rather than as a third peer.
                  Nothing is lost by taking it: both invite screens exist
                  unchanged at Dashboard → Users and Dashboard → Tenants. */}
                      <InviteForm
                        allowedRoles={[ROLES.PROPERTY_MANAGER, ROLES.CONCIERGE]}
                        buildings={buildings}
                        trailingAction={
                          <GhostButton
                            type="button"
                            disabled={busy}
                            onClick={primaryAction}
                          >
                            Continue <RightArrow />
                          </GhostButton>
                        }
                      />
                      <CenteredAction>
                        <GhostButton
                          type="button"
                          disabled={busy}
                          onClick={finish}
                        >
                          Skip invitations for now
                        </GhostButton>
                      </CenteredAction>
                      <HelperText>
                        You can invite your team and residents at any time from
                        the dashboard.
                      </HelperText>
                    </>
                  )}

                  {step.key === 'invite_tenants' && (
                    <>
                      <Heading>Invite tenants</Heading>
                      <Subheading>
                        Residents get their own portal. Invite them by email or
                        QR — they set their own password.
                      </Subheading>
                      <StateSwitch
                        state={
                          !buildingsLoaded
                            ? 'loading'
                            : buildings.length
                              ? 'ready'
                              : 'empty'
                        }
                      >
                        {!buildingsLoaded ? (
                          <SkeletonGroup label="Loading buildings">
                            <FieldSkeleton />
                            <FieldSkeleton />
                            <FieldSkeleton />
                          </SkeletonGroup>
                        ) : buildings.length ? (
                          <InviteForm
                            allowedRoles={[ROLES.RESIDENT]}
                            buildings={buildings}
                          />
                        ) : (
                          <InfoBanner>
                            Add a building first to invite tenants — you can
                            always do this later from Dashboard → Tenants.
                          </InfoBanner>
                        )}
                      </StateSwitch>
                      <ButtonRow>
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={finish}
                        >
                          Finish setup <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                      <HelperText>
                        Residents can be invited later from Dashboard → Tenants.
                      </HelperText>
                    </>
                  )}

                  {/* ── property_manager / concierge / resident flows ── */}
                  {step.key === 'profile' && (
                    <>
                      <Heading>
                        {firstName
                          ? `Welcome, ${firstName}`
                          : 'Confirm your profile'}
                      </Heading>
                      <Subheading>
                        Quick check — make sure your name and contact details
                        are right.
                      </Subheading>
                      <ProfileStep onSaved={() => advance('profile')} />
                    </>
                  )}

                  {step.key === 'building_tour' && (
                    <>
                      <Heading>Your buildings</Heading>
                      <Subheading>
                        These are the buildings in your account — you’ll log
                        visitors and daily activity for them.
                      </Subheading>
                      <InfoBanner>
                        {buildings.length
                          ? buildings.map(b => b.name).join(' · ')
                          : 'No buildings yet — your admin will add them.'}
                      </InfoBanner>
                      <ButtonRow>
                        {goBack ? (
                          <GhostButton
                            type="button"
                            disabled={busy}
                            onClick={goBack}
                          >
                            Back
                          </GhostButton>
                        ) : (
                          <span />
                        )}
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={primaryAction}
                        >
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  )}

                  {step.key === 'first_visitor_log' && (
                    <>
                      <Heading>Logging visitors</Heading>
                      <Subheading>
                        From your main menu you can log every guest, delivery,
                        and daily activity in a few taps.
                      </Subheading>
                      <InfoBanner>
                        Look for Visitor and Building Activities on your main
                        menu.
                      </InfoBanner>
                      <ButtonRow>
                        {goBack ? (
                          <GhostButton
                            type="button"
                            disabled={busy}
                            onClick={goBack}
                          >
                            Back
                          </GhostButton>
                        ) : (
                          <span />
                        )}
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={primaryAction}
                        >
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  )}

                  {step.key === 'portal_tour' && (
                    <>
                      <Heading>Your resident portal</Heading>
                      <Subheading>
                        See what’s happening in your building and keep your
                        details up to date — all in one place.
                      </Subheading>
                      <InfoBanner>
                        You can update your profile any time from your portal.
                      </InfoBanner>
                      <ButtonRow>
                        {goBack ? (
                          <GhostButton
                            type="button"
                            disabled={busy}
                            onClick={goBack}
                          >
                            Back
                          </GhostButton>
                        ) : (
                          <span />
                        )}
                        <PrimaryButton
                          type="button"
                          disabled={busy}
                          onClick={primaryAction}
                        >
                          {primaryLabel} <RightArrow />
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  )}
                </StepTransition>
              </>
            )}
          </StateSwitch>
        </AutoHeight>
      </Card>
    </Shell>
  );
};

/* A STAYS-ONLY OWNER'S FIRST RUN IS THE STAYS SETUP GUIDE (Independent Stays
   IS3). Their flow (`stays_host`: verify the email, set up the first listing)
   is never a wall and has no screens here — /onboarding sends them to the
   guide, whose step 1 adds their first property. Every other flow renders as
   before. */
export const OnboardingPage: React.FC = () => {
  const { onboarding } = useAuth();
  if (onboarding?.flow === 'stays_host') return <Navigate to="/dashboard/stays/setup" replace />;

  return <OnboardingFlowPage />;
};
